import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface WebTrendSource {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  last_fetched_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WebTrend {
  id: string;
  title: string;
  summary: string | null;
  url: string;
  source_name: string | null;
  source_type: 'rss' | 'web_search';
  source_id: string | null;
  published_at: string | null;
  image_url: string | null;
  status: 'nueva' | 'usada' | 'descartada';
  used_in_article_id: string | null;
  imported_at: string;
}

// ---- Sources ----
export function useWebTrendSources() {
  return useQuery({
    queryKey: ['web_trend_sources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('web_trend_sources')
        .select('*')
        .order('name');
      if (error) throw error;
      return (data ?? []) as WebTrendSource[];
    },
  });
}

export function useUpsertWebTrendSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<WebTrendSource> & { name: string; url: string }) => {
      if (input.id) {
        const { error } = await supabase
          .from('web_trend_sources')
          .update({ name: input.name, url: input.url, enabled: input.enabled ?? true })
          .eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('web_trend_sources')
          .insert({ name: input.name, url: input.url, enabled: input.enabled ?? true });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web_trend_sources'] }),
  });
}

export function useDeleteWebTrendSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('web_trend_sources').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web_trend_sources'] }),
  });
}

// ---- Trends ----
export function useWebTrends(status?: 'nueva' | 'usada' | 'descartada' | 'all') {
  return useQuery({
    queryKey: ['web_trends', status ?? 'all'],
    queryFn: async () => {
      let q = supabase.from('web_trends').select('*').order('published_at', { ascending: false, nullsFirst: false }).order('imported_at', { ascending: false }).limit(500);
      if (status && status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as WebTrend[];
    },
  });
}

export function useUpdateWebTrendStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: WebTrend['status'] }) => {
      const { error } = await supabase.from('web_trends').update({ status }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web_trends'] }),
  });
}

export function useFetchWebTrends() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('fetch-web-trends', { body: {} });
      if (error) throw error;
      return data as { rss_count: number; web_count: number; new_count: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['web_trends'] }),
  });
}

export function useGenerateTrendArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { trend_ids: string[]; category_id?: string | null; suggested_title?: string | null; hook?: string | null; include_cover_image?: boolean }) => {
      const { data, error } = await supabase.functions.invoke('generate-trend-article', { body: input });
      if (error) throw error;
      return data as { article_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['web_trends'] });
      qc.invalidateQueries({ queryKey: ['news-articles-admin'] });
    },
  });
}

export interface TrendProposal {
  title: string;
  hook: string;
  suggested_category_id: string | null;
  attractiveness_score: number;
  trend_ids: string[];
  trends: { id: string; title: string; source_name: string | null; url: string }[];
}

export function useProposeTrendArticles() {
  return useMutation({
    mutationFn: async (input: { date_from: string; date_to: string; status?: 'nueva' | 'all' }) => {
      const { data, error } = await supabase.functions.invoke('propose-trend-articles', { body: input });
      if (error) throw error;
      return data as { proposals: TrendProposal[]; trend_count: number; categories: { id: string; name: string }[] };
    },
  });
}

// ---- Search prompt config ----
const PROMPT_KEY = 'web_trends_search_prompt';
export function useWebTrendsSearchPrompt() {
  return useQuery({
    queryKey: ['platform_settings', PROMPT_KEY],
    queryFn: async (): Promise<{ prompt: string }> => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', PROMPT_KEY)
        .maybeSingle();
      if (error) throw error;
      return { prompt: ((data?.value as any)?.prompt as string) ?? '' };
    },
  });
}

export function useUpdateWebTrendsSearchPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (prompt: string) => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: PROMPT_KEY, value: { prompt } as any }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform_settings', PROMPT_KEY] }),
  });
}
